import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import {
	AlertTriangle,
	ArrowRightLeft,
	Bell,
	Check,
	CheckCheck,
	Loader2,
	MessageSquare,
	Users,
} from "lucide-react";
import useSound from "use-sound";

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { cn } from "../../lib/utils";
import { toast } from "react-toastify";

import TicketListItem from "../TicketListItem";
import AnnouncementsMarkdown from "../AnnouncementsMarkdown";

import openSocket from "../../services/socket-io";
import api from "../../services/api";
import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { usuarioPodeVerOuMencao } from "../../helpers/permissaoTicket";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const PRIORIDADES_AVISOS = {
	1: { rotulo: "Normal", variante: "outline-solid" },
	2: { rotulo: "Importante", variante: "secondary" },
	3: { rotulo: "Urgente", variante: "destructive" },
};

const NotificationCenter = () => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { user } = useContext(AuthContext);

	const [isOpen, setIsOpen] = useState(false);
	const [aba, setAba] = useState("all");

	// --- Tickets não lidos ---
	const { tickets } = useTickets({ withUnreadMessages: "true" });
	const [ticketsCache, setTicketsCache] = useState([]);

	useEffect(() => {
		setTicketsCache(Array.isArray(tickets) ? tickets : []);
	}, [tickets]);

	// --- Avisos (Announcements) ---
	const [avisos, setAvisos] = useState([]);
	const [unreadCountAvisos, setUnreadCountAvisos] = useState(0);
	const [loadingAvisos, setLoadingAvisos] = useState(false);
	const ackingIdAvisos = useRef(new Set());
	const [ackingAllAvisos, setAckingAllAvisos] = useState(false);

	const buscarAvisos = useCallback(
		async (silent = false) => {
			if (!user?.id) return;
			if (!silent) setLoadingAvisos(true);
			try {
				const { data } = await api.get("/announcements-unread");
				const arr = Array.isArray(data?.announcements) ? data.announcements : [];
				setAvisos(arr);
				setUnreadCountAvisos(Number(data?.unreadCount || 0));
			} catch (_) {
				/* silencioso */
			}
			if (!silent) setLoadingAvisos(false);
		},
		[user?.id],
	);

	useEffect(() => {
		buscarAvisos();
	}, [buscarAvisos]);

	const ackAviso = useCallback(
		async (announcementId) => {
			if (!user?.id) return;
			if (ackingIdAvisos.current.has(announcementId)) return;
			ackingIdAvisos.current.add(announcementId);
			try {
				await api.post(`/announcements/${announcementId}/ack`);
				setAvisos((prev) => prev.filter((a) => +a.id !== +announcementId));
				setUnreadCountAvisos((c) => Math.max(0, c - 1));
			} catch (_) {
				buscarAvisos(true);
			} finally {
				ackingIdAvisos.current.delete(announcementId);
			}
		},
		[user?.id, buscarAvisos],
	);

	const ackTodosAvisos = useCallback(async () => {
		if (!user?.id) return;
		setAckingAllAvisos(true);
		try {
			await api.post("/announcements-ack-all");
			setAvisos([]);
			setUnreadCountAvisos(0);
		} catch (_) {
			buscarAvisos(true);
		}
		setAckingAllAvisos(false);
	}, [user?.id, buscarAvisos]);

	// --- Transferências (Solicitações de transferência pendentes para mim) ---
	const [pedidosTransf, setPedidosTransf] = useState([]);
	const [respondendoTransfId, setRespondendoTransfId] = useState(null);

	const carregarTransferencias = useCallback(() => {
		if (!user?.id) return Promise.resolve();
		return api
			.get("/transfer-requests")
			.then(({ data }) => setPedidosTransf(data?.recebidas || []))
			.catch(() => undefined);
	}, [user?.id]);

	useEffect(() => {
		carregarTransferencias();
	}, [carregarTransferencias]);

	const responderTransferencia = useCallback(
		async (pedido, aceitar) => {
			if (!pedido) return;
			setRespondendoTransfId(pedido.id);
			try {
				await api.post(`/transfer-requests/${pedido.id}/${aceitar ? "accept" : "ignore"}`);
				setPedidosTransf((anteriores) =>
					anteriores.filter((p) => p.id !== pedido.id),
				);
				if (aceitar) {
					toast.success(
						`Atendimento transferido para ${pedido.requester?.name || "o colega"}.`,
					);
				} else {
					toast.info(
						`Solicitação de ${pedido.requester?.name || "transferência"} ignorada.`,
					);
				}
			} catch (err) {
				toastError(err);
				carregarTransferencias();
			}
			setRespondendoTransfId(null);
		},
		[carregarTransferencias],
	);

	// --- Contador total (badge) ---
	const totalBadge = useMemo(() => {
		const t = ticketsCache.length;
		const a = unreadCountAvisos;
		const tr = pedidosTransf.length;
		return t + a + tr;
	}, [ticketsCache, unreadCountAvisos, pedidosTransf]);

	// --- Som + Desktop Notification (mensagens novas) ---
	// RACE FIX: useRef de volume sempre guarda o VALOR MAIS NOVO (render
	// atualiza a ref sincronamente). Os listeners do socket (closures
	// antigas) leem ESTA ref na hora do play, nunca uma variável closure
	// (que poderia ser 50% do paint inicial, antes do /me retornar 0%).
	const [, setDesktopNotifications] = useState([]);
	const notifVolume01Bruto =
		user && typeof user?.notificationVolume !== "undefined" && user?.notificationVolume !== null
			? Number(user.notificationVolume)
			: 50;
	const notifVolume01 = Math.max(0, Math.min(1, notifVolume01Bruto / 100));
	const notifVolume01Ref = useRef(notifVolume01);
	// useEffect SEM rodar condicional: sempre atualiza a ref (qualquer
	// mudança de render / notificationVolume / user chegando do /me).
	notifVolume01Ref.current = notifVolume01;

	const [play] = useSound(alertSound, { volume: notifVolume01 });
	const soundRef = useRef();
	const navigateRef = useRef(navigate);
	const ticketIdRef = useRef(+pathname.split("/")[2]);

	useEffect(() => {
		soundRef.current = play;
	}, [play]);

	useEffect(() => {
		ticketIdRef.current = +pathname.split("/")[2];
	}, [pathname]);

	// --- Socket.IO (unifica os 3 componentes) ---
	useEffect(() => {
		if (!user?.id) return undefined;

		const socket = openSocket();

		const onConnect = () => {
			try {
				socket.emit("joinNotification");
			} catch (_) {}
		};

		const onTicket = (data) => {
			// ----------------------------------------------------------
			// CORREÇÃO FUGA TICKETS OUTROS ATENDENTES (NotificationCenter)
			// Não guardamos no cache de notificações tickets que o
			// usuário logado não tem permissão de ver.
			// ----------------------------------------------------------
			if (data?.ticket && !usuarioPodeVerOuMencao(user, data.ticket)) {
				if (data.action !== "delete") {
					setTicketsCache((prev) => prev.filter((t) => Number(t.id) !== Number(data.ticket.id)));
				}
				return;
			}
			if (data?.action === "updateUnread" || data?.action === "delete") {
				setTicketsCache((prev) => {
					const idx = prev.findIndex((t) => t.id === data.ticketId);
					if (idx !== -1) {
						const copia = [...prev];
						copia.splice(idx, 1);
						return copia;
					}
					return prev;
				});
			}
		};

		const onTicketMention = (payload) => {
			if (!payload?.ticketId || payload?.action !== "mention") return;
			const mention = payload.mention || {};

			setTicketsCache((prev) => {
				const idx = prev.findIndex((t) => +t.id === +payload.ticketId);
				if (idx !== -1) {
					const copia = [...prev];
					copia[idx] = {
						...copia[idx],
						unreadMessages: Number(copia[idx].unreadMessages || 0) + 1,
						unreadMentions: Number(copia[idx].unreadMentions || 0) + 1,
					};
					return copia;
				}
				const fake = {
					id: payload.ticketId,
					companyId: mention.companyId,
					status: "open",
					userId: mention.userId,
					unreadMessages: 1,
					unreadMentions: 1,
					lastMessage: mention.body?.slice(0, 120) || "",
					contact: mention.ticketContactName
						? { name: mention.ticketContactName }
						: null,
					contactName: mention.ticketContactName || null,
					createdAt: mention.createdAt,
					updatedAt: mention.createdAt,
				};
				return [fake, ...prev];
			});

			const jaNesseTicket =
				ticketIdRef.current && +ticketIdRef.current === +payload.ticketId;
			const visivel = document.visibilityState === "visible";
			if (jaNesseTicket && visivel) return;

			try {
				const title = "Nova menção em nota interna";
				const body = mention.body
					? mention.body.length > 120
						? mention.body.slice(0, 120) + "…"
						: mention.body
					: "Clique para abrir o atendimento";
				// vNotif = cálculo do volume NA HORA (usa REF, não closure).
				// Usado tanto para o Howler quanto para o Notification API do
				// navegador (silent flag — evita o som PADRÃO do SO).
				const vRawN = Number(notifVolume01Ref.current);
				const vPlayN = Number.isFinite(vRawN)
					? Math.max(0, Math.min(1, vRawN))
					: 0;
				const ehMudo = vPlayN <= 0.01;
				const options = {
					body,
					tag: `mention-${payload.ticketId}`,
					renotify: true,
					// CHAVE DO BUG QUE FALTAVA: Notification API do Chrome
					// toca o SOM PADRÃO DO SISTEMA OPERACIONAL (independente
					// do Howler sound.mp3). O silent:true PRECISA ser passado
					// quando usuário colocou mudo. Senão o browser apita o
					// som padrão do Windows/macOS/Linux mesmo bloqueando o
					// Howler aqui dentro.
					silent: ehMudo,
				};
				const n = new Notification(title, options);
				n.onclick = (e) => {
					e.preventDefault();
					window.focus();
					navigateRef.current(`/tickets/${payload.ticketId}`);
				};
				setDesktopNotifications((prev) => [n, ...prev]);
				// HOWLER BUG PARANOICO FIX (threshold 1%):
				if (!ehMudo) {
					soundRef.current?.({ volume: vPlayN });
				}
			} catch (_) {}
		};

		const onAppMessage = (data) => {
			if (
				data?.action === "create" &&
				!data.message?.read &&
				(data.ticket?.userId === user?.id || !data.ticket?.userId)
			) {
				setTicketsCache((prev) => {
					const idx = prev.findIndex((t) => t.id === data.ticket.id);
					if (idx !== -1) {
						const copia = [...prev];
						copia[idx] = data.ticket;
						return copia;
					}
					return [data.ticket, ...prev];
				});

				const skip =
					(data.message?.ticketId === ticketIdRef.current &&
						document.visibilityState === "visible") ||
					(data.ticket?.userId && data.ticket.userId !== user?.id);
				if (skip) return;

				const { message, contact, ticket } = data;
				try {
					// volume NA HORA (ref, não closure). Usado para Howler E
					// para a flag silent da Notification API (evita som padrão
					// do sistema operacional que não passa pelo Howler).
					const vRaw = Number(notifVolume01Ref.current);
					const vPlay = Number.isFinite(vRaw)
						? Math.max(0, Math.min(1, vRaw))
						: 0;
					const ehMudo = vPlay <= 0.01;
					const options = {
						body: `${message.body} - ${format(new Date(), "HH:mm")}`,
						icon: contact?.profilePicUrl,
						tag: String(ticket.id),
						renotify: true,
						// BUG FINAL QUE FALTAVA: som padrão do navegador/SO
						// (independente do Howler). Silent=true no mudo.
						silent: ehMudo,
					};
					const n = new Notification(
						`${i18n.t("tickets.notification.message")} ${contact?.name || "Contato"}`,
						options,
					);
					n.onclick = (e) => {
						e.preventDefault();
						window.focus();
						navigateRef.current(`/tickets/${ticket.id}`);
					};
					setDesktopNotifications((prev) => [n, ...prev]);
					if (!Boolean(user?.muteGroupSounds) || !Boolean(ticket?.isGroup)) {
						// HOWLER BUG PARANOICO FIX (threshold 1%):
						if (!ehMudo) {
							soundRef.current?.({ volume: vPlay });
						}
					}
				} catch (_) {}
			}
		};

		const onAnnouncements = (payload) => {
			if (!payload) return;
			if (payload.action === "refreshBadge") buscarAvisos(true);
		};

		const onTransferRequest = (dados) => {
			if (dados?.action === "create" && dados?.pedido) {
				setPedidosTransf((anteriores) =>
					anteriores.some((p) => p.id === dados.pedido.id)
						? anteriores
						: [...anteriores, dados.pedido],
				);
				return;
			}
			if (dados?.action === "accepted") {
				toast.success(
					`${dados.pedido?.holder?.name || "O atendente"} transferiu ${
						dados.pedido?.ticket?.contact?.name || "o contato"
					} para você.`,
				);
				carregarTransferencias();
				return;
			}
			if (dados?.action === "ignored") {
				toast.info(
					`${dados.pedido?.holder?.name || "O atendente"} não transferiu ${
						dados.pedido?.ticket?.contact?.name || "o contato"
					} agora.`,
				);
				carregarTransferencias();
			}
		};

		socket.on("connect", onConnect);
		socket.on("ticket", onTicket);
		socket.on("ticket:mention", onTicketMention);
		socket.on("appMessage", onAppMessage);
		socket.on("announcements", onAnnouncements);
		socket.on("transferRequest", onTransferRequest);

		if (socket.connected) onConnect();

		return () => {
			try {
				socket.off("connect", onConnect);
			} catch (_) {}
			try {
				socket.off("ticket", onTicket);
			} catch (_) {}
			try {
				socket.off("ticket:mention", onTicketMention);
			} catch (_) {}
			try {
				socket.off("appMessage", onAppMessage);
			} catch (_) {}
			try {
				socket.off("announcements", onAnnouncements);
			} catch (_) {}
			try {
				socket.off("transferRequest", onTransferRequest);
			} catch (_) {}
		};
	}, [user?.id, user?.muteGroupSounds, user?.notificationVolume, buscarAvisos, carregarTransferencias]);

	const abrirFechar = (abrir) => {
		setIsOpen(abrir);
		if (abrir) {
			// Ao abrir, força refresh silencioso das abas com backend.
			buscarAvisos(true);
			carregarTransferencias();
		}
	};

	const AbaHeader = ({ titulo, contagem, children }) => (
		<div className="flex items-center justify-between gap-2 border-b px-3 py-2">
			<div className="flex items-center gap-2">
				{children}
				<p className="text-sm font-medium">{titulo}</p>
				{contagem > 0 && (
					<Badge variant="destructive" className="h-5 px-1.5 text-[10px]">
						{contagem > 99 ? "99+" : contagem}
					</Badge>
				)}
			</div>
		</div>
	);

	return (
		<Popover open={isOpen} onOpenChange={abrirFechar}>
			<PopoverTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className="relative"
					aria-label="Central de notificações"
				>
					<Bell className="h-5 w-5" />
					{totalBadge > 0 && (
						<span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold tabular-nums text-destructive-foreground">
							{totalBadge > 99 ? "99+" : totalBadge}
						</span>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent
				align="end"
				className="w-[min(30rem,calc(100vw-2rem))] p-0"
			>
				<Tabs value={aba} onValueChange={setAba}>
					<div className="border-b px-3 py-2">
						<TabsList className="w-full justify-start overflow-x-auto">
							<TabsTrigger value="all" className="text-xs md:text-sm">
								Todos
								{totalBadge > 0 && (
									<Badge
										variant="destructive"
										className="ml-1.5 h-4 px-1 text-[9px] align-middle"
									>
										{totalBadge > 99 ? "99+" : totalBadge}
									</Badge>
								)}
							</TabsTrigger>
							<TabsTrigger value="tickets" className="text-xs md:text-sm">
								<MessageSquare className="mr-1 h-3.5 w-3.5" />
								Tickets {ticketsCache.length > 0 && `· ${ticketsCache.length}`}
							</TabsTrigger>
							<TabsTrigger value="avisos" className="text-xs md:text-sm">
								<AlertTriangle className="mr-1 h-3.5 w-3.5 text-primary" />
								Avisos {unreadCountAvisos > 0 && `· ${unreadCountAvisos}`}
							</TabsTrigger>
							<TabsTrigger value="transferencias" className="text-xs md:text-sm">
								<ArrowRightLeft className="mr-1 h-3.5 w-3.5 text-amber-500" />
								Transf. {pedidosTransf.length > 0 && `· ${pedidosTransf.length}`}
							</TabsTrigger>
						</TabsList>
					</div>

					{/* ============ ABA: TODOS ============ */}
					<TabsContent value="all" className="mt-0">
						<div className="max-h-[60vh] overflow-y-auto">
							{totalBadge === 0 && (
								<p className="px-3 py-8 text-center text-xs text-muted-foreground">
									Nenhuma notificação nova.
								</p>
							)}

							{pedidosTransf.length > 0 && (
								<section>
									<AbaHeader titulo="Transferências pendentes" contagem={pedidosTransf.length}>
										<ArrowRightLeft className="h-3.5 w-3.5 text-amber-500" />
									</AbaHeader>
									<ListaTransferencias
										pedidos={pedidosTransf}
										respondendoId={respondendoTransfId}
										responder={responderTransferencia}
										onAbrirTicket={(tid) => {
											setIsOpen(false);
											navigate(`/tickets/${tid}`);
										}}
									/>
								</section>
							)}

							{ticketsCache.length > 0 && (
								<section>
									<AbaHeader titulo="Mensagens não lidas" contagem={ticketsCache.length}>
										<MessageSquare className="h-3.5 w-3.5" />
									</AbaHeader>
									<ListaTickets
										tickets={ticketsCache}
										onAbrir={(tid) => {
											setIsOpen(false);
											navigate(`/tickets/${tid}`);
										}}
									/>
								</section>
							)}

							{avisos.length > 0 && (
								<section>
									<AbaHeader titulo="Avisos não lidos" contagem={avisos.length}>
										<AlertTriangle className="h-3.5 w-3.5 text-primary" />
									</AbaHeader>
									<ListaAvisos
										avisos={avisos}
										loading={loadingAvisos}
										ackingAll={ackingAllAvisos}
										ackingIds={ackingIdAvisos.current}
										ackOne={ackAviso}
										ackAll={ackTodosAvisos}
									/>
								</section>
							)}
						</div>
					</TabsContent>

					{/* ============ ABA: TICKETS ============ */}
					<TabsContent value="tickets" className="mt-0">
						<div className="max-h-[60vh] overflow-y-auto">
							{ticketsCache.length === 0 ? (
								<p className="px-3 py-8 text-center text-xs text-muted-foreground">
									Nenhuma mensagem não lida.
								</p>
							) : (
								<ListaTickets
									tickets={ticketsCache}
									onAbrir={(tid) => {
										setIsOpen(false);
										navigate(`/tickets/${tid}`);
									}}
								/>
							)}
						</div>
					</TabsContent>

					{/* ============ ABA: AVISOS ============ */}
					<TabsContent value="avisos" className="mt-0">
						<div className="border-b px-3 py-2">
							<div className="flex items-center justify-between gap-2">
								<div className="flex items-center gap-2">
									<AlertTriangle className="h-4 w-4 text-primary" />
									<p className="text-sm font-medium">Avisos internos</p>
									{unreadCountAvisos > 0 && (
										<Badge
											variant="destructive"
											className="ml-0 align-middle"
										>
											{unreadCountAvisos > 99 ? "99+" : unreadCountAvisos}
										</Badge>
									)}
								</div>
								{avisos.length > 0 && (
									<Button
										variant="ghost"
										size="sm"
										className="h-7 gap-1 text-xs"
										onClick={ackTodosAvisos}
										disabled={ackingAllAvisos}
									>
										{ackingAllAvisos ? (
											<Loader2 className="h-3.5 w-3.5 animate-spin" />
										) : (
											<CheckCheck className="h-3.5 w-3.5" />
										)}
										Ciente de tudo
									</Button>
								)}
							</div>
						</div>
						<div className="max-h-[60vh] overflow-y-auto">
							{loadingAvisos && (
								<p className="px-3 py-8 text-center text-xs text-muted-foreground">
									Carregando avisos…
								</p>
							)}
							{!loadingAvisos && avisos.length === 0 && (
								<p className="px-3 py-8 text-center text-xs text-muted-foreground">
									Nenhum aviso novo.
								</p>
							)}
							{!loadingAvisos && (
								<ListaAvisos
									avisos={avisos}
									loading={loadingAvisos}
									ackingAll={ackingAllAvisos}
									ackingIds={ackingIdAvisos.current}
									ackOne={ackAviso}
									ackAll={ackTodosAvisos}
									esconderBotaoTodos
								/>
							)}
						</div>
					</TabsContent>

					{/* ============ ABA: TRANSFERÊNCIAS ============ */}
					<TabsContent value="transferencias" className="mt-0">
						<AbaHeader titulo="Solicitações de transferência" contagem={pedidosTransf.length}>
							<ArrowRightLeft className="h-3.5 w-3.5 text-amber-500" />
						</AbaHeader>
						<div className="max-h-[60vh] overflow-y-auto">
							{pedidosTransf.length === 0 && (
								<p className="px-3 py-8 text-center text-xs text-muted-foreground">
									Nenhuma solicitação de transferência pendente.
								</p>
							)}
							<ListaTransferencias
								pedidos={pedidosTransf}
								respondendoId={respondendoTransfId}
								responder={responderTransferencia}
								onAbrirTicket={(tid) => {
									setIsOpen(false);
									navigate(`/tickets/${tid}`);
								}}
							/>
						</div>
					</TabsContent>
				</Tabs>
			</PopoverContent>
		</Popover>
	);
};

// --- Subcomponentes (para reduzir duplicação entre aba Todos e aba específica) ---

function ListaTickets({ tickets, onAbrir }) {
	return (
		<div>
			{tickets.map((t) => (
				<div key={t.id} onClick={() => onAbrir(t.id)}>
					<TicketListItem ticket={t} />
				</div>
			))}
		</div>
	);
}

function ListaAvisos({
	avisos,
	ackingIds,
	ackOne,
	ackAll,
	loading,
	ackingAll,
	esconderBotaoTodos,
}) {
	return (
		<div>
			{avisos.map((a) => {
				const p = PRIORIDADES_AVISOS[a.priority] || PRIORIDADES_AVISOS[1];
				const acking = ackingIds?.has(a.id);
				const eGlobal = Boolean(a.isGlobal);
				return (
					<article
						key={a.id}
						className={cn(
							"flex flex-col gap-2 border-b p-3 last:border-b-0",
							eGlobal && "bg-destructive/[0.03]",
						)}
					>
						<header className="flex items-start justify-between gap-2">
							<div className="min-w-0 flex-1">
								<div className="mb-1 flex flex-wrap items-center gap-1.5">
									{!eGlobal && (
										<Badge
											variant="default"
											className="h-5 gap-1 bg-sky-600/90 px-2 text-[10px] text-sky-50 hover:bg-sky-600"
										>
											<Users className="h-3 w-3" />
											AVISO INTERNO EQUIPE
										</Badge>
									)}
									<Badge variant={p.variante} className="h-5 text-[10px]">
										{p.rotulo}
									</Badge>
								</div>
								<h4 className="truncate text-sm font-semibold leading-tight">
									{a.title}
								</h4>
								<div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
									<span>{eGlobal ? "Equipe Perabit" : a.user?.name || "Sistema"}</span>
									<span>·</span>
									{a.createdAt && (
										<span>{format(parseISO(a.createdAt), "dd/MM HH:mm")}</span>
									)}
								</div>
							</div>
							<Button
								variant="outline"
								size="sm"
								className="h-7 shrink-0 gap-1 text-xs"
								onClick={() => ackOne(a.id)}
								disabled={acking || loading}
							>
								{acking ? (
									<Loader2 className="h-3.5 w-3.5 animate-spin" />
								) : (
									<Check className="h-3.5 w-3.5" />
								)}
								Ciente
							</Button>
						</header>
						<div className="rounded-md bg-muted/40 p-2">
							<AnnouncementsMarkdown>{a.text}</AnnouncementsMarkdown>
						</div>
					</article>
				);
			})}
			{!esconderBotaoTodos && avisos.length > 1 && (
				<div className="border-t p-2">
					<Button
						variant="ghost"
						size="sm"
						className="h-7 w-full gap-1 text-xs"
						onClick={ackAll}
						disabled={ackingAll || loading}
					>
						{ackingAll ? (
							<Loader2 className="h-3.5 w-3.5 animate-spin" />
						) : (
							<CheckCheck className="h-3.5 w-3.5" />
						)}
						Marcar todos os avisos como ciente
					</Button>
				</div>
			)}
		</div>
	);
}

function ListaTransferencias({ pedidos, respondendoId, responder, onAbrirTicket }) {
	return (
		<div>
			{pedidos.map((p) => {
				const processando = respondendoId === p.id;
				const contato = p.ticket?.contact;
				return (
					<article
						key={p.id}
						className="flex flex-col gap-2 border-b p-3 last:border-b-0"
					>
						<header className="flex items-start justify-between gap-2">
							<div className="min-w-0 flex-1">
								<div className="mb-1 flex items-center gap-1.5">
									<Badge
										variant="secondary"
										className="h-5 gap-1 px-2 text-[10px]"
									>
										<ArrowRightLeft className="h-3 w-3 text-amber-500" />
										SOLICITAÇÃO
									</Badge>
									<span className="text-[11px] text-muted-foreground">
										Ticket #{p.ticketId}
										{p.ticket?.queue?.name ? ` · ${p.ticket.queue.name}` : ""}
									</span>
								</div>
								<p className="text-sm font-semibold">{contato?.name || "Contato"}</p>
								{contato?.number && (
									<p className="text-[11px] text-muted-foreground">
										{contato.number}
									</p>
								)}
								<p className="mt-1 text-xs">
									<span className="font-medium text-amber-600 dark:text-amber-400">
										{p.requester?.name || "Um atendente"}
									</span>{" "}
									está solicitando este contato.
								</p>
							</div>
						</header>
						<div className="flex flex-wrap gap-2">
							<Button
								variant="outline"
								size="sm"
								className="h-7 text-xs"
								onClick={() => responder(p, false)}
								disabled={processando}
							>
								{processando ? (
									<Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
								) : null}
								Ignorar
							</Button>
							<Button
								size="sm"
								className="h-7 gap-1 text-xs"
								onClick={() => responder(p, true)}
								disabled={processando}
							>
								<ArrowRightLeft className="h-3.5 w-3.5" />
								Transferir
							</Button>
							<Button
								variant="ghost"
								size="sm"
								className="h-7 text-xs text-muted-foreground"
								onClick={() => onAbrirTicket(p.ticketId)}
							>
								Ver a conversa
							</Button>
						</div>
					</article>
				);
			})}
		</div>
	);
}

export default NotificationCenter;
